/**
 * System Settings Tab Component
 * Configure system-wide preferences
 */
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save } from "lucide-react";

export function SystemSettingsTab({
  settings,
  onChange,
  onSave,
  saving
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="system-settings-tab">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">System Preferences</h2>
          <p className="text-sm text-slate-500">Configure system-wide settings</p>
        </div>
        <Button onClick={onSave} disabled={saving} className="gap-2">
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="font-medium text-slate-800">Regional Settings</h3>
          <div className="space-y-2">
            <Label>Timezone</Label>
            <Select
              value={settings.timezone || "America/New_York"}
              onValueChange={(val) => onChange({ ...settings, timezone: val })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                <SelectItem value="America/Anchorage">Alaska Time (AKT)</SelectItem>
                <SelectItem value="Pacific/Honolulu">Hawaii Time (HT)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date Format</Label>
              <Select
                value={settings.date_format || "MM/DD/YYYY"}
                onValueChange={(val) => onChange({ ...settings, date_format: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                  <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                  <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Time Format</Label>
              <Select
                value={settings.time_format || "12h"}
                onValueChange={(val) => onChange({ ...settings, time_format: val })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="12h">12-hour (AM/PM)</SelectItem>
                  <SelectItem value="24h">24-hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-medium text-slate-800">Session & Data</h3>
          <div className="space-y-2">
            <Label>Session Timeout (minutes)</Label>
            <Select
              value={String(settings.session_timeout || 60)}
              onValueChange={(val) => onChange({ ...settings, session_timeout: parseInt(val) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
                <SelectItem value="480">8 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Audit Log Retention (days)</Label>
            <Select
              value={String(settings.audit_log_retention || 365)}
              onValueChange={(val) => onChange({ ...settings, audit_log_retention: parseInt(val) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="180">180 days</SelectItem>
                <SelectItem value="365">1 year</SelectItem>
                <SelectItem value="730">2 years</SelectItem>
                <SelectItem value="1825">5 years</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-700">Allow Concurrent Sessions</p>
              <p className="text-sm text-slate-500">Users can log in from multiple devices</p>
            </div>
            <Switch
              checked={settings.allow_concurrent_sessions || false}
              onCheckedChange={(checked) => 
                onChange({ ...settings, allow_concurrent_sessions: checked })
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
